import type { MethodGraph } from '../schemas/index.js';
import { NAMESPACES, NODE_TYPES, REFERENCE_TERMS } from '../vocabulary/index.js';
import { compareStrings, orderNodeKeys, sortNodes } from './canonical.js';

/**
 * The method graph in the generic node/edge dialect that graph tools accept.
 *
 * This is a *second* serialisation, not a replacement. `serialiseMethodGraph` emits the
 * canonical form — `@id`/`@type` with `dpem:`-prefixed classes and one predicate per
 * relation — and that form is the round-trip unit: import parses it, publishing hashes it,
 * compare diffs it (architecture §8.8). Generic graph loaders do not read it, because they
 * key on `id`/`type` and recognise a small fixed set of node types. So the same graph is
 * offered a second way, and the difference is entirely mechanical:
 *
 *   `@id`  → `id`                     (aliased in `@context`, so the IRIs do not move)
 *   `@type` → `type`, mapped onto {Entity, Actor, Role}
 *   hasPhase / performedBy / produces … → one `relatesTo` array
 *   every other property              → a string entry in `attributes`
 *
 * Nothing is dropped and nothing is invented. Each folded edge also keeps its predicate in
 * `attributes` under the predicate's own name, so a reader that cares *which* relation an edge
 * was can still recover it, and one that does not gets a graph that loads.
 *
 * Two rules here exist because a loader rejected the alternative, not because they read
 * better:
 *
 *   - **Attribute keys carry no colon.** `relatesTo:hasPhase` looks like a compact IRI to a
 *     JSON-LD tokeniser, and `relatesTo` is a term (`dpem:relatesTo`) rather than a prefix, so
 *     the key parses as neither a CURIE nor an IRI and strict readers reject the document. The
 *     predicate name alone — `hasPhase` — is a plain term and parses everywhere.
 *   - **Arity survives.** A single-valued property stays a bare string; a list becomes a JSON
 *     array in a string. Wrapping a scalar in `["…"]` would tell a reader the ontology allows
 *     many where it allows one.
 *
 * The fold collapses sixteen ontology classes onto three `type` values, but it does not lose
 * them: `attributes.dpemType` carries the class back, and every predicate keeps its own name
 * and its arity. That is what `interop-parse.ts` reads, so a user who downloads this dialect
 * can import the same file — and it is why the shape rules here are checked rather than
 * asserted (`interop-compliance.ts`).
 */

/** Node types the interchange dialect recognises. */
export const INTEROP_NODE_TYPES = ['Entity', 'Actor', 'Role', 'User'] as const;
export type InteropNodeType = (typeof INTEROP_NODE_TYPES)[number];

/**
 * Type aliases rather than interfaces, deliberately: only an alias gets TypeScript's implicit
 * index signature, which is what lets an interop document be handed to anything typed
 * `Record<string, unknown>` — the transport shape the export response declares — without a
 * cast at the boundary.
 */
export type InteropGraphNode = {
  readonly id: string;
  readonly type: InteropNodeType;
  readonly name: string;
  /** Absent rather than synthesised: a made-up sentence is worse than a missing key. */
  readonly description?: string;
  /** Absent rather than empty, which is how the dialect's own documents read. */
  readonly relatesTo?: readonly string[];
  /** Always present, because `dpemType` is always in it. */
  readonly attributes: Readonly<Record<string, string>>;
};

export type InteropGraphDocument = {
  readonly '@context': Readonly<Record<string, string>>;
  readonly '@graph': readonly InteropGraphNode[];
};

/**
 * `dpem:Role` and `dpem:Agent` have exact counterparts; everything else is a concept in
 * the graph and becomes `Entity`.
 *
 * `Agent → Actor` rather than `Entity` because an agent is something that *acts* — it is the
 * non-human half of the same distinction `Role` draws. `User` is in the dialect's vocabulary
 * and unused here on purpose: the ontology models roles a person can hold, never the person,
 * so nothing in a method graph is a user account.
 */
const INTEROP_TYPES: Readonly<Record<string, InteropNodeType>> = {
  [NODE_TYPES.Role]: 'Role',
  [NODE_TYPES.Agent]: 'Actor',
};

const REFERENCE_TERM_NAMES: ReadonlySet<string> = new Set(
  REFERENCE_TERMS.map((term) => term.term),
);

const RESERVED_KEYS: ReadonlySet<string> = new Set(['@id', '@type', 'name', 'description']);

/** The ontology class the node came from, kept because `type` alone cannot say. */
const ONTOLOGY_TYPE_KEY = 'dpemType';

/**
 * Set when `name` was derived from the id rather than authored.
 *
 * The dialect requires a name on every node; the ontology does not — a template version and a
 * tailoring decision may go unnamed. So one is derived, and the fact that it was derived is
 * recorded, because otherwise re-importing the export would write `V0` into a node whose name
 * the author had deliberately left empty. One flag is cheaper than a round-trip that quietly
 * invents author data.
 */
const DERIVED_NAME_KEY = 'nameFromId';

export type InteropSerialiseOptions = {
  /** JSON indentation. 2 matches every other serialisation in the platform. */
  readonly indent?: number;
};

/** `dpem:Stage` → `Stage`, for a node with no name of its own. */
function localName(iri: string): string {
  const separator = iri.lastIndexOf(':');
  return separator < 0 ? iri : iri.slice(separator + 1);
}

function scalarText(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') return String(value);
  return JSON.stringify(value);
}

/**
 * One property, one string — with the plurality it had.
 *
 * Lists are written with a space after each comma rather than by `JSON.stringify` alone: the
 * dialect's own documents are spaced that way, and an attribute value is read by people at
 * least as often as by machines.
 */
function attributeValue(value: unknown): string {
  if (!Array.isArray(value)) return scalarText(value);
  return `[${value.map((item) => JSON.stringify(scalarText(item))).join(', ')}]`;
}

/**
 * Namespaces for every prefix the document uses — its node ids *and* everything those nodes
 * point at.
 *
 * The canonical export leaves these undeclared — `stage:S1` carries no `stage` term, which
 * strictly reads as an IRI in a `stage` scheme rather than a compact id, and `expandIri`
 * documents that as the thing which keeps legacy ids byte-stable. Compacting them here would
 * change identity, so instead each prefix is declared against the ontology namespace: the
 * ids stay the exact strings they were, and a strict JSON-LD processor can now resolve them.
 *
 * References and not only ids, because a prefix can appear on one side only: a variant's
 * `variantOf` names a `dpse:` parent that a single-method export does not carry as a node, and
 * a prefix used in the graph but missing from the context is a compliance error on its own.
 *
 * `dpem` is the ontology namespace itself, so `dpem:DPEM` expands where it always did; every
 * other prefix becomes a segment of it.
 */
function prefixNamespaces(curies: readonly string[]): Record<string, string> {
  const prefixes = new Set<string>();
  for (const curie of curies) {
    const separator = curie.indexOf(':');
    if (separator > 0) prefixes.add(curie.slice(0, separator));
  }
  prefixes.delete('dpem');
  const namespaces: Record<string, string> = {};
  for (const prefix of [...prefixes].sort(compareStrings)) {
    namespaces[prefix] = `${NAMESPACES.dpem}${prefix}-`;
  }
  return namespaces;
}

function interopContext(curies: readonly string[]): Record<string, string> {
  return {
    id: '@id',
    type: '@type',
    dpem: NAMESPACES.dpem,
    schema: NAMESPACES.schema,
    // `schema:name`, not the canonical form's `rdfs:label`: this dialect declares two
    // namespaces, and borrowing both terms from one of them is one fewer thing to resolve.
    name: 'schema:name',
    description: 'schema:description',
    attributes: 'dpem:attributes',
    relatesTo: 'dpem:relatesTo',
    ...prefixNamespaces(curies),
  };
}

function interopNode(node: Record<string, unknown>): InteropGraphNode {
  const id = String(node['@id']);
  const nodeType = String(node['@type']);

  const relatesTo: string[] = [];
  const attributes: Record<string, string> = { [ONTOLOGY_TYPE_KEY]: nodeType };

  const authoredName = typeof node.name === 'string' && node.name.length > 0 ? node.name : undefined;
  if (authoredName === undefined) attributes[DERIVED_NAME_KEY] = 'true';

  // Canonical key order in, deterministic attribute and edge order out: same graph, same
  // bytes, which is the property every other serialisation in the platform has.
  for (const [key, value] of Object.entries(orderNodeKeys(node))) {
    if (RESERVED_KEYS.has(key)) continue;
    attributes[key] = attributeValue(value);

    if (!REFERENCE_TERM_NAMES.has(key)) continue;
    const targets = (Array.isArray(value) ? value : [value]).filter(
      (target): target is string => typeof target === 'string',
    );
    // Unresolved targets included, not filtered. A variant's `variantOf` names a parent this
    // export does not contain, and dropping the edge would misreport the method as having no
    // parent — a target outside the document is still what the graph says.
    for (const target of targets) if (!relatesTo.includes(target)) relatesTo.push(target);
  }

  const description = typeof node.description === 'string' ? node.description : '';

  return {
    id,
    type: INTEROP_TYPES[nodeType] ?? 'Entity',
    name: authoredName ?? localName(id),
    ...(description.length === 0 ? {} : { description }),
    ...(relatesTo.length === 0 ? {} : { relatesTo }),
    attributes,
  };
}

/** Every CURIE the document mentions, so the context can declare all of their prefixes. */
function curiesIn(nodes: readonly InteropGraphNode[]): readonly string[] {
  const curies: string[] = [];
  for (const node of nodes) {
    curies.push(node.id, node.attributes[ONTOLOGY_TYPE_KEY] ?? '');
    if (node.relatesTo !== undefined) curies.push(...node.relatesTo);
  }
  return curies;
}

/** The method graph in the interchange dialect, in canonical node order. */
export function serialiseInteropGraph(graph: MethodGraph): InteropGraphDocument {
  const source = sortNodes(graph['@graph']) as unknown as readonly Record<string, unknown>[];
  const nodes = source.map((node) => interopNode(node));

  return {
    '@context': interopContext(curiesIn(nodes)),
    '@graph': nodes,
  };
}

export function serialiseInteropGraphToJson(
  graph: MethodGraph,
  options: InteropSerialiseOptions = {},
): string {
  const indent = options.indent ?? 2;
  return `${JSON.stringify(serialiseInteropGraph(graph), null, indent)}\n`;
}
